/**
 * CPU-extruded 2D shadow volumes: the stencil geometry for a `Light2D` under
 * `shadow_filter = NONE`, whose one `step()` in `canvas.glsl` is a hard test. A filtered
 * light samples `shadowPolarMap.ts` instead (ADR-0030), and `PointLight2D`'s
 * gate picks one. Everything is in the previewer's 2D world space, Y up.
 */
import { OccluderPolygon2DCullMode } from '../../godot/scene/lightOccluder2d.js';
/** The interval `points` cover along `axis`. */
function project(points, axis) {
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i + 1 < points.length; i += 2) {
        const along = axis.x * points[i] + axis.y * points[i + 1];
        if (along < min)
            min = along;
        if (along > max)
            max = along;
    }
    return [min, max];
}
function overlaps([aMin, aMax], [bMin, bMax]) {
    return aMin <= bMax && aMax >= bMin;
}
/** A quad's two edge normals, unnormalised: enough to separate it, as its opposite edges are parallel. */
export function edgeNormals([a, b, c]) {
    return [
        { x: a.y - b.y, y: b.x - a.x },
        { x: b.y - c.y, y: c.x - b.x },
    ];
}
/**
 * A test of whether bounds meet the convex `polygon` (`[x,y, …]`), whose edge normals are
 * `polygonAxes`. Both are convex, so a separating axis among the two shapes' edge normals decides
 * it, as Godot's `Rect2::intersects_transformed` does (`renderer_viewport.cpp:544`). The polygon's
 * own intervals are projected once, for every bounds the test meets. Bounds that are not finite
 * meet nothing.
 */
export function convexBoundsTest(polygon, polygonAxes) {
    const polygonIntervals = polygonAxes.map((axis) => project(polygon, axis));
    return (bounds) => {
        const [a, b, c, d] = bounds;
        const corners = [a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y];
        if (!corners.every(Number.isFinite))
            return false;
        return (polygonAxes.every((axis, index) => overlaps(project(corners, axis), polygonIntervals[index])) &&
            edgeNormals(bounds).every((axis) => overlaps(project(corners, axis), project(polygon, axis))));
    };
}
/**
 * How far past the light's reach a volume's far cap is pushed. The two-chord cap
 * comes within `far · cos(θ/4)` of the light, and θ ≤ π for a casting edge, so
 * √2 is the exact requirement. 1.5 keeps a margin without losing float precision.
 */
const CAP_MARGIN = 1.5;
/** Vertices emitted per casting edge: a 5-gon fanned into 3 triangles. */
const VERTICES_PER_EDGE = 9;
/**
 * Twice the signed area of triangle (light, a, b). Zero means the light is
 * collinear with the edge, whichever side of it the light lies on.
 */
function orientation(ax, ay, bx, by, lx, ly) {
    return (ax - lx) * (by - ly) - (ay - ly) * (bx - lx);
}
/**
 * Does edge a→b cast a shadow from a light at (lx, ly) under `cullMode`? The
 * winding modes take the edges facing toward or away from the light, with signs
 * that negate Godot's Y-down convention. Measured: a closed convex polygon under
 * CULL_COUNTER_CLOCKWISE shadows only from its far edges.
 */
export function edgeCastsShadow(ax, ay, bx, by, lx, ly, cullMode) {
    const orient = orientation(ax, ay, bx, by, lx, ly);
    // A collinear edge has no shadow area, and this also covers a light on the
    // edge's line, in front of it or between its endpoints.
    if (!Number.isFinite(orient) || orient === 0)
        return false;
    if (cullMode === OccluderPolygon2DCullMode.CLOCKWISE)
        return orient > 0;
    if (cullMode === OccluderPolygon2DCullMode.COUNTER_CLOCKWISE)
        return orient < 0;
    return true;
}
/**
 * The convex ring `[a, b, bFar, mFar, aFar]` of one edge's shadow volume, or null
 * when it has no area. The edge is the near boundary. `mFar` caps the far ends
 * along the bisector, so a wide wedge's cap cannot cut back into the lit area.
 * Measured: Godot's unfiltered boundary lands on the light→endpoint ray within ~1 px.
 */
function edgeShadowRing(ax, ay, bx, by, light, reach) {
    const { x: lx, y: ly } = light;
    const dax = ax - lx;
    const day = ay - ly;
    const dbx = bx - lx;
    const dby = by - ly;
    const da = Math.hypot(dax, day);
    const db = Math.hypot(dbx, dby);
    if (!(da > 0) || !(db > 0))
        return null;
    const uax = dax / da;
    const uay = day / da;
    const ubx = dbx / db;
    const uby = dby / db;
    // Parallel rays mean a wedge of zero angle: a zero-length edge, an edge
    // pointing straight away from the light, or (antiparallel) the light sitting
    // between the endpoints. None of them shadow any area.
    if (uax * uby - uay * ubx === 0)
        return null;
    let mx = uax + ubx;
    let my = uay + uby;
    const ml = Math.hypot(mx, my);
    if (!(ml > 0))
        return null;
    mx /= ml;
    my /= ml;
    // Past the light's reach and past both endpoints, so the cap never lands
    // between the light and the edge for an occluder outside the lit rect.
    const far = CAP_MARGIN * Math.max(reach, da, db);
    if (!Number.isFinite(far))
        return null;
    return new Float32Array([
        ax,
        ay,
        bx,
        by,
        lx + ubx * far,
        ly + uby * far,
        lx + mx * far,
        ly + my * far,
        lx + uax * far,
        ly + uay * far,
    ]);
}
/** Longest distance from the shadow origin to a corner of the light's rect. */
function lightReach(light) {
    const { rect } = light;
    const dx = Math.max(Math.abs(rect.minX - light.x), Math.abs(rect.maxX - light.x));
    const dy = Math.max(Math.abs(rect.minY - light.y), Math.abs(rect.maxY - light.y));
    return Math.hypot(dx, dy);
}
const RECT_AXES = [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
];
/**
 * Godot's occluder cull for one light: the test that keeps an occluder only while its bounds overlap
 * the light's rect (`renderer_canvas_render_rd.cpp:1056`, `p_light_rect.intersects_transformed`).
 */
export function lightRectTest({ minX, minY, maxX, maxY }) {
    return convexBoundsTest([minX, minY, maxX, minY, maxX, maxY, minX, maxY], RECT_AXES);
}
/**
 * Every casting edge's volume as one non-indexed `[x,y,z, …]` buffer, z 0, or
 * null when nothing casts. Drawn with face culling off under a `REPLACE` stencil,
 * so winding and overlap need no resolving. Under NONE this beats the polar
 * map's 0.18° bins at the one-pixel step: the goldens measure ~1/255 mean.
 */
export function buildShadowVolumes(light, casters) {
    const reach = lightReach(light);
    const rings = [];
    const inLightRect = lightRectTest(light.rect);
    for (const caster of casters) {
        const { segments, cullMode, bounds } = caster;
        if (!inLightRect(bounds))
            continue;
        for (let i = 0; i + 3 < segments.length; i += 4) {
            const ax = segments[i];
            const ay = segments[i + 1];
            const bx = segments[i + 2];
            const by = segments[i + 3];
            if (!edgeCastsShadow(ax, ay, bx, by, light.x, light.y, cullMode))
                continue;
            const ring = edgeShadowRing(ax, ay, bx, by, light, reach);
            if (ring)
                rings.push(ring);
        }
    }
    if (rings.length === 0)
        return null;
    const out = new Float32Array(rings.length * VERTICES_PER_EDGE * 3);
    let o = 0;
    for (const ring of rings) {
        // Fan the convex 5-gon from vertex 0: (0,1,2) (0,2,3) (0,3,4).
        for (let t = 1; t + 1 < 5; t++) {
            for (const v of [0, t, t + 1]) {
                out[o] = ring[v * 2];
                out[o + 1] = ring[v * 2 + 1];
                out[o + 2] = 0;
                o += 3;
            }
        }
    }
    return out;
}
//# sourceMappingURL=shadowVolumes.js.map